import { PageHeader } from "@/components/page-header/PageHeader";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { css, Global } from "@emotion/react";
import { PriceListFilterInput } from "@/gql/graphql";
import { Indent, Row, Spacing } from "@stenajs-webui/core";
import {
  Banner,
  Card,
  CardBody,
  CardHeader,
  FlatButton,
  stenaDownload,
  stenaTodoList,
} from "@stenajs-webui/elements";
import { useContext, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useFetchPriceList } from "../hooks/UseFetchPriceList";
import { validatePriceListFilter } from "../lib/priceListFilterInput";
import { PriceListFilter } from "./price-list-filter/PriceListFilter";
import {
  PriceListFilterContext,
  PriceListFilterContextType,
  PriceListFilterProvider,
} from "./price-list-filter/PriceListFilterContext";
import { PriceListTable } from "./PriceListTable";
import { SteadySpinner } from "./SteadySpinner";
import { pluralize } from "@/lib/formatters/GeneralStringFormatting";
import { downloadExcelForStandardTable } from "@stenajs-webui/grid-export";
import { createPriceListExcelConfig } from "../lib/priceListExcelConfig";
import { useResolveVehicleTypeName } from "../hooks/UseResolveVehicleTypeName";

const PriceListMessage = ({ text }: { text: string }) => {
  const { setFilterIsOpen } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;

  return (
    <Row spacing={4} justifyContent={"center"}>
      <Banner
        variant={"info"}
        text={text}
        contentRight={
          <FlatButton
            label={"Open filter"}
            onClick={() => setFilterIsOpen(true)}
          />
        }
      />
    </Row>
  );
};

const PriceListError = ({ message }: { message: string }) => (
  <Row spacing={4} justifyContent={"center"}>
    <Banner variant={"error"} headerText={message} />
  </Row>
);

const PriceListScreenContent = () => {
  const { filterState } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  const { currency } = useCurrencySelection();

  const [appliedFilter, setAppliedFilter] = useState<PriceListFilterInput>();

  const { rows, loading, error } = useFetchPriceList(appliedFilter);

  const handleSearch = () => {
    const validation = validatePriceListFilter(filterState, currency);
    if (validation.valid) {
      setAppliedFilter(validation.input);
    }
  };

  const handleClear = () => setAppliedFilter(undefined);

  const exportFilename = `price-list${
    appliedFilter?.date ? `-${appliedFilter.date}` : ""
  }`;

  const resolveVehicleTypeName = useResolveVehicleTypeName();
  const excelConfig = useMemo(
    () => createPriceListExcelConfig(resolveVehicleTypeName),
    [resolveVehicleTypeName]
  );

  const tableContent = error ? (
    <PriceListError message={error.message} />
  ) : loading && rows.length === 0 ? (
    <Spacing num={4}>
      <SteadySpinner />
    </Spacing>
  ) : rows.length === 0 ? (
    <PriceListMessage
      text={
        appliedFilter
          ? "No price rows to show"
          : "Please select a filter to search"
      }
    />
  ) : undefined;

  return (
    <>
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
        `}
      />
      <Helmet title="Price list - FPP" />
      <PageHeader title="Price list" icon={stenaTodoList} />
      <PageLayout>
        <Card>
          <CardHeader
            text={
              rows.length > 0
                ? pluralize(rows.length, " price row", " price rows")
                : "Price rows"
            }
          />
          <Row alignItems={"center"} justifyContent={"space-between"}>
            <PriceListFilter onSearch={handleSearch} onClear={handleClear} />
            {rows.length > 0 && (
              <Indent num={3}>
                <FlatButton
                  label={"Export to Excel"}
                  leftIcon={stenaDownload}
                  onClick={() =>
                    downloadExcelForStandardTable(
                      exportFilename,
                      excelConfig,
                      rows
                    )
                  }
                />
              </Indent>
            )}
          </Row>
          <CardBody>
            <PriceListTable
              rows={rows}
              content={tableContent}
              resetKey={appliedFilter}
            />
          </CardBody>
        </Card>
      </PageLayout>
    </>
  );
};

export const PriceListScreen = () => {
  return (
    <PriceListFilterProvider>
      <PriceListScreenContent />
    </PriceListFilterProvider>
  );
};
